/**
 * Living Documentation — Story-based, auto-generated from E2E tests.
 *
 * Pattern: Document-Driven Testing (DDT)
 * Each chapter tells a user story with steps, screenshots, and status.
 * Reading this page feels like a product walkthrough, not a test report.
 */
import { useEffect, useState } from "react";
import { fettText } from "../lib/doku-fett";
import { WeeklyThresholdGuide } from "../components/WeeklyThresholdGuide";

interface Step {
  step: string;
  status: "PASS" | "FAIL" | "PENDING" | "INFO";
  detail: string;
  screenshot?: string;
}

interface Chapter {
  id: string;
  title: string;
  story?: string;
  intro?: string;
  steps: Step[];
}

interface DocData {
  timestamp: string;
  title: string;
  intro?: string;
  chapters: Chapter[];
  summary: { total_steps: number; pass: number; fail: number; pending: number; info: number };
}

const INITIAL_BASE = document.baseURI;

const STATUS_CFG: Record<string, { icon: string; label: string; cls: string }> = {
  PASS: { icon: "✅", label: "Funktioniert", cls: "doc-pass" },
  FAIL: { icon: "❌", label: "Fehlgeschlagen", cls: "doc-fail" },
  PENDING: { icon: "🔨", label: "In Entwicklung", cls: "doc-pending" },
  INFO: { icon: "ℹ️", label: "Hinweis", cls: "doc-info" },
};

export function DocumentationPage() {
  const [data, setData] = useState<DocData | null>(null);
  const [openChapter, setOpenChapter] = useState<string | null>(null);
  const [zoomedImg, setZoomedImg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetch(new URL("api/docs/test-results", INITIAL_BASE).toString(), { credentials: "same-origin" })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); if (d.chapters?.length) setOpenChapter(d.chapters[0].id); })
      .catch(() => setError("Test-Ergebnisse nicht gefunden."));
  }, []);

  if (error) return (
    <div className="doc-page">
      <h1>Funktionsdokumentation</h1>
      <WeeklyThresholdGuide />
      <div className="empty-state"><h3>Keine Daten</h3><p className="muted">{error}</p></div>
    </div>
  );
  if (!data) return <div className="doc-page"><h1>Funktionsdokumentation</h1><WeeklyThresholdGuide /><p className="muted">Weitere Funktionsdokumentation wird geladen…</p></div>;

  const doneChapters = data.chapters.filter((ch) => ch.steps.every((s) => s.status === "PASS" || s.status === "INFO"));
  const wipChapters = data.chapters.filter((ch) => ch.steps.some((s) => s.status === "PENDING"));

  return (
    <div className="doc-page">
      <header className="doc-header">
        <h1>{data.title}</h1>
        <p className="doc-subtitle">
          {data.intro ?? "Dieses Handbuch beschreibt die Funktionen des Sibit Personalverrechnungs-Tools."}
          <br />Stand: {new Date(data.timestamp).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
        </p>
      </header>

      <WeeklyThresholdGuide />

      <section className="doc-meta">
        <div className="doc-meta-card">
          <div className="doc-meta-label">Kapitel</div>
          <div className="doc-meta-value">{data.chapters.length}</div>
        </div>
        <div className="doc-meta-card doc-meta-done">
          <div className="doc-meta-label">Dokumentiert</div>
          <div className="doc-meta-value">{doneChapters.length}</div>
        </div>
        {wipChapters.length > 0 && (
          <div className="doc-meta-card doc-meta-wip">
            <div className="doc-meta-label">In Arbeit</div>
            <div className="doc-meta-value" title={wipChapters.map((c) => c.title).join(", ")}>{wipChapters.length}</div>
          </div>
        )}
      </section>

      <nav className="doc-toc">
        <strong>Kapitel</strong>
        {data.chapters.map((ch) => {
          const done = ch.steps.every((s) => s.status === "PASS" || s.status === "INFO");
          const wip = ch.steps.some((s) => s.status === "PENDING");
          return (
            <button key={ch.id} className={`doc-toc-item${openChapter === ch.id ? " active" : ""}`}
              onClick={() => {
                setOpenChapter(ch.id);
                setTimeout(() => document.getElementById(`ch-${ch.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
              }}>
              <span>{done ? "✅" : wip ? "🔨" : "❌"}</span> {ch.title}
            </button>
          );
        })}
      </nav>

      <section className="doc-chapters">
        {data.chapters.map((ch) => (
          <article key={ch.id} id={`ch-${ch.id}`} className={`doc-chapter${openChapter === ch.id ? " open" : ""}`}>
            <div className="doc-chapter-header" onClick={() => {
              const next = openChapter === ch.id ? null : ch.id;
              setOpenChapter(next);
              if (next) setTimeout(() => document.getElementById(`ch-${ch.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            }}>
              <h2>{ch.title}</h2>
              <span className="doc-chapter-toggle">{openChapter === ch.id ? "▼" : "▶"}</span>
            </div>
            {openChapter === ch.id && (
              <div className="doc-chapter-body">
                <p className="doc-intro">{ch.intro ?? ch.story}</p>
                <ol className="doc-steps">
                  {ch.steps.map((s, i) => {
                    const cfg = STATUS_CFG[s.status] || STATUS_CFG.INFO!;
                    return (
                      <li key={i} className={`doc-step ${cfg.cls}`}>
                        <div className="doc-step-head">
                          <span className="doc-step-icon">{cfg.icon}</span>
                          <div>
                            <div className="doc-step-text">{fettText(s.step)}</div>
                            {s.detail && <div className="doc-step-detail">{fettText(s.detail)}</div>}
                          </div>
                        </div>
                        {s.screenshot && (
                          <div className={`doc-step-img${zoomedImg === s.screenshot ? " zoomed" : ""}`}
                            onClick={() => setZoomedImg(zoomedImg === s.screenshot ? null : (s.screenshot ?? null))}>
                            <img src={new URL(`api/docs/screenshots/${s.screenshot?.split("/").pop()}`, INITIAL_BASE).toString()}
                              alt={s.step} loading="lazy" />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </article>
        ))}
      </section>

      <footer className="doc-footer">
        <p className="muted">
          Bedienungsanleitung und ergänzende Dokumentation aus Funktionstests.
        </p>
      </footer>

      {showBackToTop && (
        <button
          className="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          title="Zum Anfang"
        >
          ↑ Übersicht
        </button>
      )}
    </div>
  );
}
